import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COMMON, TYPOGRAPHY } from "@tSurya/theme/design";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import * as React from "react";
import { css } from "styled-components";

export interface BrandNavigationStyles {
    container: InjectableCss;
    linksListContainer: InjectableCss;
    linksList: InjectableCss;
    linkItem: TypographyPresentationProps;
    activeTypographyProps: TypographyPresentationProps;
    link: InjectableCss;
}

export const getBrandNavigationStyles = (textColor?: string): BrandNavigationStyles => ({
    container: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
            gap: 32px;
            padding: 8px 40px;

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 8px 16px;
                        `,
                        null,
                        null,
                        css`
                            padding: 8px 40px;
                        `,
                        null
                    ]
                );
            }}
        `,
    },
    linksListContainer: {
        css: css`
            width: 100%;
            max-width: ${isThemeActive(THEME_IDS.MGBW) ? 'none' : `${COMMON.width.content}px`};
            margin: ${isThemeActive(THEME_IDS.MGBW) ? '0' : '0 auto'};
            padding: 0;
            overflow-x: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;

            &::-webkit-scrollbar {
                display: none;
            }

            @media (max-width: 1280px) {
                max-width: 100%;
            }

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            padding: 0;
                        `,
                        css`
                            padding: 0;
                        `,
                    ]
                );
            }}
        `
    },
    linksList: {
        css: css`
            display: flex;
            gap: 32px;
            list-style: none;
            margin: 0;
            padding: 0;
            font-size: ${TYPOGRAPHY.size.smaller};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            gap: 12px;
                        `,
                        null,
                        null,
                        null,
                        null
                    ]
                )}
        `,
    },
    linkItem: {
        color: textColor || "#FFFFFF",
        css: css`
            color: inherit;
            font-family: 'Lato', sans-serif;
            font-style: normal;
            font-weight: 500;
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: 1.5;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            position: relative;
            padding-bottom: 0;
            border-bottom: 2px solid transparent;
            transition: border-bottom-color 0.3s ease;
            display: inline-block;
            white-space: nowrap;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: ${TYPOGRAPHY.mobileBrandNavigationLink.size};
                            letter-spacing: 0.08px;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ]
                )}
        `,
    },
    activeTypographyProps: {
        color: textColor || "#FFFFFF",
        css: css`
            color: inherit;
            font-family: 'Lato', sans-serif;
            font-style: normal;
            font-weight: 700;
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: 1.5;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            position: relative;
            padding-bottom: 0;
            border-bottom: 2px solid currentColor;
            transition: border-bottom-color 0.3s ease;
            display: inline-block;
            white-space: nowrap;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: ${TYPOGRAPHY.mobileBrandNavigationLink.size};
                            letter-spacing: 0.08px;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ]
                )}
        `,
    },
    link: {
        css: css`
            white-space: nowrap;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.mobileBrandNavigationLink.size};
                        `,
                        null,
                        null,
                        null,
                    ]
                )}
        `,
    },
});

export const brandNavigationStyles: BrandNavigationStyles = getBrandNavigationStyles();

const enum fields {
    brandLinks = "brandLinks",
    backgroundColor = "backgroundColor",
    textColor = "textColor",
}

interface BrandLink {
    fields: {
        text: string;
        url: string;
        isActive: boolean;
        openInNewTab: boolean;
    };
}

interface OwnProps {
    fields: {
        [fields.brandLinks]: BrandLink[];
        [fields.backgroundColor]: string;
        [fields.textColor]: string;
    };
}

type Props = WidgetProps & OwnProps;

const BrandNavigation: React.FC<Props> = ({ 
    id, 
    fields: {
        brandLinks = [],
        backgroundColor,
        textColor,
    },
}) => {
    const styles = getBrandNavigationStyles(textColor);
    
    return (
        <StyledWrapper 
            {...styles.container} 
            css={css`
                ${styles.container.css}
                ${backgroundColor ? `background-color: ${backgroundColor};` : ''}
                ${textColor ? `color: ${textColor};` : ''}
            `}
        >
            <StyledWrapper {...styles.linksListContainer}>
                <StyledWrapper
                    as="ul"
                    {...styles.linksList}
                >
                    {brandLinks.map((link, index) => (
                        <StyledWrapper
                            key={`${link.fields.url}-${link.fields.text}-${index}`}
                            as="li"
                        >
                            <Link 
                                href={link.fields.url || "#"}
                                typographyProps={link.fields.isActive ? styles.activeTypographyProps : styles.linkItem}
                                color={textColor || styles.linkItem?.color}
                                hoverStyle={{ color: textColor || styles.linkItem?.color }}
                                target={link.fields.openInNewTab ? "_blank" : undefined}
                                rel={link.fields.openInNewTab ? "noopener noreferrer" : undefined}
                                css={css`
                                    ${textColor ? `color: ${textColor} !important;` : ''}
                                `}
                            >
                                {link.fields.text}
                            </Link>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BrandNavigation,
    definition: {
        group: "Header",
        icon: "magnifying-glass",
        displayName: "Brand Navigation",
        fieldDefinitions: [
            {
                name: "backgroundColor",
                displayName: "Background Color",
                editorTemplate: "TextField",
                defaultValue: "#1C1917",
                fieldType: "General",
                tooltip: "css color value",
            },
            {
                name: "textColor",
                displayName: "Text Color",
                editorTemplate: "TextField",
                defaultValue: "#ffffff",
                fieldType: "General",
                tooltip: "css color value",
            },
            {
                name: "brandLinks",
                displayName: "Brand Links",
                editorTemplate: "ListField",
                getDisplay: (item: any) => item.fields?.text || "New Link",
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "text",
                        displayName: "Link Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "url",
                        displayName: "Link URL",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "isActive",
                        displayName: "Is Active",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "openInNewTab",
                        displayName: "Open in New Tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;